import { useState } from "react";
import { useTranslation } from "react-i18next";

import { namespaces } from "../../consts/i18n";
import CloseButton from "../buttons/CloseButton";
import TextButton from "../buttons/TextButton";
import DialogWrapper from "./DialogWrapper";
import CircleButton from "../buttons/CircleButton";
import { GrayWrapper } from "./GrayWrapper";
import TextInput from "../inputs/TextInput";
import {
  useCreateTechnicianMutation,
  useDeleteTechnicianMutation,
  useListTechniciansQuery,
} from "../../api/managementApi";

const ManageTechniciansDialog = ({ isOpen, onClose, projectId }) => {
  const [name, setName] = useState("");

  const { t } = useTranslation(namespaces.pages);

  const { data: technicians, isLoading } = useListTechniciansQuery({
    projectId,
  });

  const [createTechnician] = useCreateTechnicianMutation();

  const [deleteTechnician] = useDeleteTechnicianMutation();

  const handleClose = (e) => {
    e.preventDefault();
    onClose();
  };

  const handleDeleteTechnician = (technicianId) => {
    deleteTechnician({ projectId, technicianId });
  };

  const handleCreateTechnician = () => {
    createTechnician({ name, projectId });
    setName("");
  };

  return isOpen ? (
    <DialogWrapper onClick={handleClose}>
      <div
        className="relative bg-white rounded-xl w-[518px] px-[65px] pt-[65px] pb-[64px]"
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton onClick={handleClose} />
        <div className="flex flex-col gap-y-[15px]">
          <div>
            <h3 className="text-xxxl font-bold text-center whitespace-pre-wrap break-words">
              {t("projectPage.manageTechniciansDialog.title")}
            </h3>
          </div>

          <div className="flex flex-col gap-y-[10px]">
            <label
              htmlFor="requiredFilesToUpload"
              className="font-bold text-primary text-sm"
            >
              {t("projectPage.manageTechniciansDialog.techniciansListLabel")}
            </label>
            <GrayWrapper className="justify-left items-left w-full">
              <div className="flex flex-col gap-y-[10px] px-[25px] max-h-[300px] overflow-y-auto">
                {!isLoading && technicians.length === 0 && (
                  <>
                    <p className="text-[14px] m-auto text-center">
                      {t(
                        "projectPage.manageTechniciansDialog.noTechnicianInList",
                      )}
                      <br />
                      <b>
                        {t(
                          "projectPage.manageTechniciansDialog.noTechnicianSelfSetup",
                        )}
                      </b>
                    </p>
                  </>
                )}
                {!isLoading &&
                  technicians.map((technician) => (
                    <div
                      className="flex flex-row justify-between"
                      key={technician.id}
                    >
                      <span className="font-bold text-black-700 text-sm py-[4px] overflow-hidden text-ellipsis max-w-[200px]">
                        {technician.name}
                      </span>
                      <CircleButton
                        icon="/images/trash.svg"
                        iconSize="16px"
                        onClick={() => handleDeleteTechnician(technician.id)}
                      />
                    </div>
                  ))}
              </div>
            </GrayWrapper>
          </div>

          <div className="flex flex-col gap-y-[10px]">
            <label
              htmlFor="requiredFilesToUpload"
              className="font-bold text-primary text-sm mb-2"
            />

            <div className="flex flex-row gap-x-[20px] items-end">
              <div className="flex-grow">
                <TextInput
                  label={t(
                    "projectPage.manageTechniciansDialog.addTechnicianLabel",
                  )}
                  value={name}
                  onChange={setName}
                  id="technicianName"
                />
              </div>
              <div className="grow-0">
                <TextButton
                  className="h-[52px]"
                  disabled={name.length === 0}
                  secondary
                  label={t(
                    "projectPage.manageTechniciansDialog.addTechnicianButton",
                  )}
                  onClick={handleCreateTechnician}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-row items-center justify-center grow-0 mt-[50px]">
            <div>
              <TextButton
                className="h-[56px]"
                label={t(
                  "projectPage.manageTechniciansDialog.closeModalDialog",
                )}
                onClick={onClose}
              />
            </div>
          </div>
        </div>
      </div>
    </DialogWrapper>
  ) : null;
};

export default ManageTechniciansDialog;
